import type { XpTransactionListParams } from 'src/types/xp';
import type { ApiGetListingsPayload } from 'src/types/listing';
import type { ApiRentalListingListParams } from 'src/types/rental';
import type { CatalogVertical, CatalogFacetsParams } from 'src/utils/catalogUrl';

export const QueryKeys = {
  // The catalogue entry an address names: a make by its slug, a model by its make and its slug.
  // The bare `catalogMakes()` / `catalogModels()` are the prefixes every lookup of a kind starts with.
  catalogMakes: () => ['catalogMake'] as const,
  catalogMake: (slug: string) => ['catalogMake', slug] as const,
  catalogModels: () => ['catalogModel'] as const,
  catalogModel: (makeId: string, slug: string) => ['catalogModel', makeId, slug] as const,
  // The makes and models a catalog's filter offers. The bare `catalogFacets(vertical)` is the
  // prefix that re-asks every set of filters of that vertical at once ("Refresh the list").
  catalogFacets: (vertical: CatalogVertical, params?: CatalogFacetsParams) =>
    params === undefined
      ? (['catalogFacets', vertical] as const)
      : (['catalogFacets', vertical, params] as const),
  // Session answers are keyed by the epoch of the tab that asked (`sessionEpoch()`, one more at
  // every sign-in and sign-out): a new session is a new key, so a person who signs in after someone
  // else on the same device is never served the previous person's `/me` — however long the answer
  // may otherwise stand (staleness.ts). The token used to be the key; since the single sign-in the
  // session is the api's cookie and no script sees it (api#454). The bare `userMe()` is the prefix
  // for invalidation and eviction.
  userMe: () => ['userMe'] as const,
  userMeFor: (epoch: number) => ['userMe', epoch] as const,
  // The account's primary wallet (`/wallet/auth/me` under the account session), keyed the same way:
  // it decides which wallet family an email change is signed with (app#243), so the next account
  // on the device must not be handed the previous one's.
  accountWallet: () => ['accountWallet'] as const,
  accountWalletFor: (epoch: number) => ['accountWallet', epoch] as const,
  // A person's own deals and notifications are read with whichever session is in force, so they are
  // keyed by it too (app#248): `session` is `requestSession()` — the tab's epoch, which names the
  // session without carrying anything of it. Appended last, so the bare keys below (`rentalDeals()`,
  // `deliveryDeal(id)`, …) stay the prefixes that invalidate every session's copy at once.
  forSession: <K extends readonly unknown[]>(base: K, session: string) =>
    [...base, { session }] as const,
  /** The session a key was made for by `forSession`; `undefined` for a key that belongs to no session. */
  sessionOf: (queryKey: readonly unknown[]): string | undefined => {
    const tail = queryKey[queryKey.length - 1];

    return typeof tail === 'object' &&
      tail !== null &&
      'session' in tail &&
      typeof tail.session === 'string'
      ? tail.session
      : undefined;
  },
  listings: (params?: ApiGetListingsPayload) => ['listings', params] as const,
  listing: (id: string) => ['listing', id] as const,
  rentalDeals: () => ['rentalDeals'] as const,
  rentalDeal: (id: string) => ['rentalDeal', id] as const,
  rentalTopUpQuote: (id: string) => ['rentalTopUpQuote', id] as const,
  rentalExtensionQuote: (id: string, days: number) => ['rentalExtensionQuote', id, days] as const,
  rentalListings: (params?: ApiRentalListingListParams) => ['rentalListings', params] as const,
  rentalListing: (id: string) => ['rentalListing', id] as const,
  rentalBookingQuote: (id: string, startDate: string, endDate: string) =>
    ['rentalBookingQuote', id, startDate, endDate] as const,
  directPurchases: () => ['directPurchases'] as const,
  directPurchase: (id: string) => ['directPurchase', id] as const,
  deliveryPoints: () => ['deliveryPoints'] as const,
  // The countries and cities a dealer can be in (api#298) — reference data, like the delivery cities.
  locations: () => ['locations'] as const,
  deliveryDeals: () => ['deliveryDeals'] as const,
  deliveryDeal: (id: string) => ['deliveryDeal', id] as const,
  buyerChains: () => ['buyerChains'] as const,
  // The Reown wallet registry behind "All wallets" (app#315): the first page, or a search's answer.
  walletRegistry: (search: string) => ['walletRegistry', search] as const,
  // Whether the installation has the storefront assistant (api#381): one answer per visit.
  assistantStatus: () => ['assistantStatus'] as const,
  renterNotifications: () => ['renterNotifications'] as const,
  xpBalance: () => ['xpBalance'] as const,
  xpTransactions: (params?: XpTransactionListParams) => ['xpTransactions', params] as const,
} as const;
